import {IRuleInfo, IRuleInfoRaw, RawRuleInfoRawStructure} from 'src/app/features/user-access/models/rule-info';
import {RuleHumanID} from '../../../features/user-access/models/rule-info.enum';
import {ENABLE_PRODUCT_MAPPING_FEATURE, ENABLE_QUOTES_FEATURE} from '../page/pages-const';

export enum AccessFlags {}

const compile = (): { structured: IRuleInfo[]; index: IRuleInfo[]; dictionary: { [humanID: string]: IRuleInfo } } => {
  const index: IRuleInfo[] = [];
  const dictionary: { [humanID: string]: IRuleInfo } = {};
  const incompleteRequiredBy: { [humanID: string]: IRuleInfo[] } = {};

  const recursiveStep = (rules: IRuleInfoRaw[] = RawRuleInfoRawStructure, parent: IRuleInfo = null): void => {
    for (const raw of rules) {
      const rule: IRuleInfo = {
        humanID: raw.humanID,
        index: index.length,
        text: raw.text,
        depend: [],
        rules: [],
        accessCode: raw.accessCode,
      };

      if (AccessFlags[rule.humanID]) {
        alert('A rule\'s `humanID` is equal to an AccessFlag enum');
        throw Error('A rule\'s `humanID` is equal to an AccessFlag enum');
      }
      index.push(rule);
      dictionary[rule.humanID] = rule;

      if (parent) {
        rule.depend.push(parent);
        parent.rules.push(rule);
      }

      if (incompleteRequiredBy[rule.humanID]) {

        for (const dependable of incompleteRequiredBy[rule.humanID]) {
          dependable.depend.push(rule);
        }
        delete incompleteRequiredBy[rule.humanID];
      }

      for (const humanID of raw.depend ? raw.depend : []) {
        if (dictionary[humanID]) {
          rule.depend.push(dictionary[humanID]);
        } else if (incompleteRequiredBy[humanID]) {
          incompleteRequiredBy[humanID].push(rule);
        } else {
          incompleteRequiredBy[humanID] = [rule];
        }
      }

      if (raw.rules) {
        recursiveStep(raw.rules, rule);
      }
    }
  };
  recursiveStep();

  if (Object.keys(incompleteRequiredBy).length) {
    throw Error(`One ore more rules dependent on humanIDs that dont exist?. Dingus\n` +
      JSON.stringify(incompleteRequiredBy));
  }
  const structured = RawRuleInfoRawStructure.map((raw) => dictionary[raw.humanID]);
  return {structured, index, dictionary};
};

export const ruleStructure = compile();

/**
 * Returns the access bitmask bit position for a compiled rule.
 */
export const getRuleAccessBitIndex = (rule: IRuleInfo): number => rule.accessCode ?? rule.index;

export const hasRule = (humanID: string, bitsAccess: (0 | 1)[]): boolean =>
  bitsAccess[ruleStructure.dictionary[humanID].index] === 1;

/** Stock take count (i.) or manager (j.) access — either rule is sufficient. */
const STOCK_TAKE_ACCESS_RULES: (RuleHumanID | AccessFlags)[] = ['i.', 'j.'];

/**
 * Maps dynamic route paths to stable switch keys (e.g. `stock-take/detail/abc` → `stock-take/detail/:id`).
 */
const normalizePageForAccessRules = (page: string): string => {
  if (page.startsWith('stock-take/mobile-detail/')) {
    return 'stock-take/mobile-detail/:id';
  }
  if (page.startsWith('stock-take/detail/')) {
    return 'stock-take/detail/:id';
  }
  if (page === 'stock-take' || page === 'stock-take/list' || page.startsWith('stock-take/')) {
    return 'stock-take';
  }
  if (page === 'quotes' || page.startsWith('quotes/')) {
    return 'quotes';
  }
  if (page === 'stock-match' || page.startsWith('stock-match/')) {
    return 'stock-match';
  }
  return page;
};

export const pageRules = (page: string): (RuleHumanID | AccessFlags)[] | false => {
  switch (normalizePageForAccessRules(page)) {
    case 'stock-take':
      return STOCK_TAKE_ACCESS_RULES;
    case 'stock-take/detail/:id':
      return ['j.'];
    case 'stock-take/mobile-detail/:id':
      return STOCK_TAKE_ACCESS_RULES;
    case 'admin-internal':
      return [];
    case 'auto-ordering':
      return ['d.'];
    case 'barcode-scanner':
    case 'barcode-scanner/home':
    case 'barcode-scanner/shelf-talkers':
    case 'barcode-scanner/shelf-talkers/print-shelf-talkers':
    case 'barcode-scanner/order/create-order':
      return ['g.'];
    case 'barcode-scanner/bin-locations/update-bin-location':
    case 'barcode-scanner/process-grv':
    case 'barcode-scanner/stock/update-stock':
      return ['b.ii'];
    case 'dashboard':
    case 'dashboard-reporting':
      return ['e.'];
    case 'dss-modal':
      return false;
    case 'home':
      return [];
    case 'login':
      return [];
    case 'manage-it/dep-sales-summary':
      return false;
    case 'messages-modal':
      return [];
    case 'ngp-report':
      return ['c.']; // ['c.', 'b.i', 'b.ii']
    case 'quotes':
      return ENABLE_QUOTES_FEATURE ? ['b.i'] : false;
    case 'settings':
      return [];
    case 'save-user-settings':
      return [];
    case 'shelf-talkers':
      return ['f.'];
    case 'signup':
      return [];
    case 'stock':
    case 'stock-manager':
    case 'stock-updates':
      return ['b.i'];
    case 'stock-match':
      return ENABLE_PRODUCT_MAPPING_FEATURE ? ['b.i'] : false;
    case 'testing':
      return [];
    case 'user-access':
      return ['a.'];
    default:
      return false;
  }
};


export const validateRule = (index: number, access: number): boolean => {
  const validated: { [i: number]: boolean } = {};

  const recursiveStep = (ruleIndex: number): boolean => {
    if (!validated.hasOwnProperty(ruleIndex)) {
      const bitIndex = getRuleAccessBitIndex(ruleStructure.index[ruleIndex]);
      if (Math.floor((access / Math.pow(2, bitIndex)) % 2) === 1) {
        validated[ruleIndex] = true;

        for (const rule of ruleStructure.index[ruleIndex].depend) {
          if (!recursiveStep(ruleIndex) && validated[ruleIndex]) {
            validated[ruleIndex] = false;
          }
        }
        return validated[ruleIndex];
      } else {
        validated[ruleIndex] = false;
      }
    }
    return validated[ruleIndex];
  };

  if (!recursiveStep(index)) {
    delete validated[index];

    if (Object.keys(validated).length > 0) {
      return validated[index];
    } else {
      return false;
    }
  }
  return true;
};
